import { Connection } from '../lib/connection'
import { API_V2_ENDPOINT, FILE_UPLOAD_URL, PRECHATFORM_ENDPOINT } from '../lib/constants'
import { navigateTo } from './navigation'
import { formIdFromFormsHubSelector } from '../selectors/hub'
import jsonp from 'jsonp'
import axios from 'axios'
import { FormValues } from '../reducers/form-values'
import { formSelector } from '../selectors/forms'
import { Form } from '../lib/form-builder/types'
const CancelToken = axios.CancelToken

export type SubmitFormResult = {
  status: string
  msg: string
}

const DATA_TYPES = [
  'textfield',
  'select',
  'radio',
  'checkbox',
  'textarea',
  'thumbs',
  'stars',
  'stars1to5',
  'fileInput'
]

function arrayifyFormValues (form: Form, values: FormValues) {
  return form
    .map((entry) => {
      const value = values[entry.qid]
      if (value.type === 'textInput') {
        return DATA_TYPES.includes(entry.field_type) && value.value
      } else if (value.type === 'fileInput') {
        return JSON.stringify(value.tokens)
      }
      return false
    })
    .filter((value) => {
      return typeof value === 'string'
    })
}

function filterObjectValues (form: Form, values: FormValues) {
  return Object.entries(values).reduce((acc, [curKey, curValue]) => {
    const formElement = form.find((entry) => entry.qid === curKey)
    if (formElement && curValue.type === 'textInput' && DATA_TYPES.includes(formElement.field_type)) {
      return {
        ...acc,
        [curKey]: curValue.value
      }
    } else if (formElement && curValue.type === 'fileInput') {
      return {
        ...acc,
        [curKey]: curValue.tokens
      }
    } else {
      return acc
    }
  }, {})
}

function querifyArray (separator: '?' | '&', name: string, arr: string[]) {
  if (arr && arr.length > 0) {
    return separator + arr.map((item) => `${name}=${encodeURIComponent(item)}`).join('&')
  } else {
    return ''
  }
}

function sendFormAsJsonP ({
  userId,
  formId,
  form,
  values,
  fileTokens,
  consentId,
  currentUrl,
  visitorUUid,
  language,
  articleId
}: {
  userId: number,
  formId: string,
  form: Form,
  values: FormValues,
  fileTokens?: string[],
  consentId: number,
  currentUrl: string,
  visitorUUid: string,
  language: string
  articleId?: number
}) {
  return new Promise((resolve, reject) => {
    const data = arrayifyFormValues(form, values)
    let url = API_V2_ENDPOINT + 'api/nkiData' +
      '?sid=' + userId +
      '&nid=' + formId +
      '&channel=null' +
      '&curl=' + currentUrl +
      '&data=' + encodeURIComponent(JSON.stringify(data)) +
      '&uid=' + visitorUUid
    if (fileTokens) {
      url += querifyArray('&', 'fileTokens', fileTokens)
    }
    if (articleId) {
      url += '&dataid=' + articleId
    }

    if (consentId) {
      url += '&consentId=' + consentId +
        '&lang=' + language
    }

    jsonp(url, {}, (err, data: SubmitFormResult) => {
      if (err) {
        return reject(err)
      }
      resolve(data)
    })
  })
}

export const submitCustomForm = (
  userId: number,
  instanceId: string,
  formId: string,
  agentId: number,
  channelId: string,
  values: FormValues,
  consentId: number,
  fileTokens: string[]) => {
  return (dispatch: Function, getState: Function, { conn }: {conn: Connection}) => {
    const currentUrl = encodeURIComponent(getState().host.url)
    const visitorUUid = getState().widget.visitorUuid
    const form = formSelector(getState(), formId)
    const language = getState().host.language

    let promise
    if (form.versionId) {
      promise = axios.post(
        API_V2_ENDPOINT + 'api/submitForm',
          {
            userId,
            formId,
            data: filterObjectValues(form.form, values),
            fileTokens,
            consentId,
            curl: currentUrl,
            visitorUUid,
            language,
            versionId: form.versionId,
            channel: channelId,
            agentId
          }
        ).then((response) => response.data)
    } else {
      promise = sendFormAsJsonP({
        userId,
        formId,
        form: form.form,
        values,
        fileTokens,
        consentId,
        currentUrl,
        visitorUUid,
        language
      })
    }

    dispatch({
      type: 'FORM_SUBMIT_FETCH',
      formId
    })

    promise.then((data: SubmitFormResult) => {
      dispatch({
        type: 'FORM_SUBMIT_SUCCESS',
        thankyouText: data.msg,
        instanceId,
        formId
      })
    }).catch((err) => {
      dispatch({
        type: 'FORM_SUBMIT_FAILED',
        instanceId,
        formId
      })
    })

    return promise
  }
}

export const submitAnswersFeedbackForm = (
  userId: number,
  instanceId: string,
  formId: number,
  articleId: number,
  values: FormValues,
  consentId: number,
  fileTokens: string[]) => {
  return (dispatch: Function, getState: Function, { conn }: {conn: Connection}) => {
      const visitorUUid = getState().widget.visitorUuid
      const currentUrl = encodeURIComponent(getState().host.url)
      const language = getState().host.language
      const form = formSelector(getState(), formId)

      let promise
      if (form.versionId) {
        promise = axios.post(
          API_V2_ENDPOINT + 'api/submitForm',
            {
              userId,
              formId,
              data: filterObjectValues(form.form, values),
              consentId,
              currentUrl,
              visitorUUid,
              language,
              versionId: form.versionId,
              articleId,
              fileTokens
            }
          ).then((response) => response.data)
      } else {
        promise = sendFormAsJsonP({
          userId,
          formId: String(formId),
          form: form.form,
          values,
          consentId,
          currentUrl,
          visitorUUid,
          language,
          articleId,
          fileTokens
        })
      }

     dispatch({
      type: 'FORM_SUBMIT_FETCH',
      formId
    })

    promise.then((data: SubmitFormResult) => {
      dispatch({
        type: 'FORM_SUBMIT_SUCCESS',
        thankyouText: data.msg,
        instanceId,
        formId
      })
    }).catch((err) => {
      dispatch({
        type: 'FORM_SUBMIT_FAILED',
        instanceId,
        formId
      })
    })

    return promise
  }
}

export const navigateToThankYou = (formId: number) => {
  return navigateTo('form', 'formThankYou', {
    formId
  })
}

export const submitPrechatForm = (form, values: string[]) => {
  return (dispatch: Function, getState: Function, { conn }: {conn: Connection}) => {
    const userId = getState().form.userId
    const channelId = getState().form.channelId

    const groupFormIndex = findGroupSelectIndex(form)
    const group = groupFormIndex !== -1 ? values[groupFormIndex] : ''
    const url = PRECHATFORM_ENDPOINT +
      '?sid=' + userId +
      '&grp=' + group

    dispatch({
      type: 'PRECHATFORM_SUBMIT_FETCH'
    })

    jsonp(url, {}, (err, data) => {
      if (err) {
        dispatch({
          type: 'PRECHATFORM_SUBMIT_FAILED'
        })
      } else {
        if (data.aID) {
          dispatch({
            type: 'INIT_OPEN_CHAT',
            userId: data.sid,
            agentId: data.aID,
            agentName: data.agentName,
            agentPicture: data.agentPicture,
            agentDepartment: data.department,
            channelId
          })
        }
      }
    })
  }
}

export const getForm = (userId: number, agentId: number, formId: number) => {
  return (dispatch: Function, getState: Function, { conn }: {conn: Connection}) => {
    return new Promise((resolve, reject) => {
      const url = `${API_V2_ENDPOINT}api/form?sid=${userId}&aid=${agentId}&nid=${formId}&channel=null`

      dispatch({
        type: 'FORM_FETCH',
        formId
      })

      axios.get(url).then((response) => {
        dispatch({
          type: 'FORM_SUCCESS',
          formId,
          data: response.data.form,
          versionId: response.data.versionId
        })
        resolve()
      })
      .catch((err: Error) => {
        dispatch({
          type: 'FORM_FAILED',
          formId,
          err
        })
        reject(err)
      })
    })

  }
}

export const navigateToForm = (userId: number, agentId: number, formId: number) => {
  return navigateTo('form', 'form', {
    userId,
    agentId,
    formId
  })
}

function findGroupSelectIndex (form) {
  for (let i = 0; i < form.length; i++) {
    if (form[i].qid === 'pcfVal') {
      return i
    }
  }
  return -1
}

export const upploadFormFile = (
  id: string,
  uniqLocalId: string,
  file: File,
  onProgress: (id: string, uniqLocalId: string, progress: number, uniqId?: string, cancel?) => void
) => {
  return (dispatch: Function, getState: Function, { conn }: {conn: Connection}): Promise<string> => {
    const data = new FormData()

    data.append('userId', getState().widget.userId)
    data.append('visitorUuid', getState().widget.visitorUuid)
    data.append('nkiId', '' + formIdFromFormsHubSelector(getState()))
    data.append('uploadfile', file, file.name)
    const url = `${FILE_UPLOAD_URL}upload/nki`
    let cancel = null

    return axios
      .post(url, data, {
        onUploadProgress: (ProgressEvent) => {
          onProgress(id, uniqLocalId, Math.round((ProgressEvent.loaded / ProgressEvent.total * 100)), '', cancel)
        },
        cancelToken: new CancelToken((c) => {
          cancel = c
        })
      })
      .then((res) => {
        onProgress(id, uniqLocalId, 100, res.data.token, null)
        return res.data.token
      })
  }
}
